<template>
  <div>
    <b-card :header="$route.name"
            bg-variant="light"
            header-bg-variant="dark"
            header-text-variant="white">
      <b-tabs no-body>
        <b-container class="pa-0"
                     fluid>
          <slot name="message"/>

          <b-row align-h="between">
            <b-col cols="auto">
              <b-btn class="mt-2"
                     size="lg"
                     variant="info"
                     @click="createNewProposal">CREATE NEW PROPOSAL
              </b-btn>
            </b-col>
            <b-col cols="auto">
              <b-btn v-b-modal.modal_ceric_user_completion
                     class="mt-2"
                     size="lg"
                     variant="info">CERIC PROPOSAL
              </b-btn>
              <modal_ceric_user_completion @set-refresh="set_refresh"/>
            </b-col>
          </b-row>
        </b-container>

        <b-tab active
               class="ma-1 mt-4"
               title="in draft">
          <proposals_list :fields="fieldsDraft"
                          :infrastructure="url.infrastructure"
                          :main-url-part="`/${url.mainUrlPart}/draft/me`"
                          :proposal-form-url-part="url.proposalFormUrlPart"/>
        </b-tab>

        <b-tab class="ma-1 mt-4"
               title="submitted">
          <proposals_list :fields="fieldsSubmitted"
                          :infrastructure="url.infrastructure"
                          :main-url-part="`/${url.mainUrlPart}/submitted/me`"
                          :proposal-form-url-part="url.proposalFormUrlPart"
                          :refresh-submitted="refresh"
                          is-submitted
                          @set-refresh="set_refresh"
                          @open-evaluation-modal="openEvaluationModal"/>
        </b-tab>
      </b-tabs>
    </b-card>

    <base-info-modal ref="evaluationModal"
                     modal-title="Proposal details">
      <template>
        <proposal-details-modal :asd-table-data="asdTableData"
                                :evaluation-table-data="evaluationTableData"
                                :is-ceric="isCurrentProposalCeric"
                                :is-rapid-access="isCurrentProposalRapidAccess"
                                :proposal="currentProposal"
                                :team-table-data="teamTableData"/>
      </template>
    </base-info-modal>
  </div>
</template>

<script>
  import proposals_list from '@/components/proposal/proposals/proposals_list';
  import modal_ceric_user_completion from '@/components/proposal/ceric/modal_ceric_user_completion';
  import { ordinalNumber } from '@/helpers/commonTableColumns';
  import ProposalDetailsModal from '@/components/proposal/ProposalDetailsModal';
  import BaseInfoModal from '@/components/base-components/base-modals/BaseInfoModal';
  import { roleFormatMixin } from '@/mixins/roleFormat';

  export default {
    components: {
      BaseInfoModal,
      ProposalDetailsModal,
      proposals_list,
      modal_ceric_user_completion,
    },
    props: {
      url: {
        type: Object,
        required: true,
      },
    },
    mixins: [roleFormatMixin],
    data() {
      return {
        fieldsDraft: [
          ordinalNumber,
          {
            key: 'title',
            label: 'Title',
            sortable: true,
          },
          {
            key: 'author',
            label: 'Author',
            sortable: true,
            class: 'custom-break-col-sm',
            thStyle: { width: '10%' },
          },
          {
            key: 'created_at',
            label: 'Creation date',
            sortable: true,
          },
          {
            key: 'actions',
            thStyle: { minWidth: '9rem' },
          },
        ],
        fieldsSubmittedToEdit: [
          ordinalNumber,
          {
            key: 'document_specification.document_id',
            label: 'ID',
            sortable: true,
            class: 'custom-column-id-width',
          },
          {
            key: 'title',
            label: 'Title',
            sortable: true,
          },
          {
            key: 'author',
            label: 'Author',
            sortable: true,
            class: 'custom-break-col-sm',
            thStyle: { width: '10%' },
          },
          {
            key: 'call_name',
            label: 'Call',
            sortable: true,
          },
          {
            key: 'proposal_type_and_subtype',
            label: 'Proposal type',
            sortable: true,
            tdClass: 'preLine',
          },
          {
            key: 'states.date',
            label: 'Proposal submission date',
            sortable: true,
            thStyle: { width: '4%' },
          },
          {
            key: 'evaluations_data.acceptance_mark',
            label: 'Proposal status',
            sortable: true,
            thStyle: { width: '4%' },
          },
          {
            key: 'actions',
            thStyle: { minWidth: '9rem' },
          },
        ],
        currentProposal: null,
        refresh: false,
      };
    },
    computed: {
      fieldsSubmitted() {
        const fieldsCopy = [...this.fieldsSubmittedToEdit];
        if (this.isSynchrotron) {
          fieldsCopy.splice(4, 0, {
            key: 'general.end_station',
            label: 'End station',
            sortable: true,
          });
        }
        return fieldsCopy;
      },
      isSynchrotron() {
        return this.url.infrastructure === 'synchrotron';
      },
      isCurrentProposalCeric() {
        const proposalSubtypesObject = this.currentProposal?.document_specification.subtype || {};
        return Object.keys(proposalSubtypesObject).includes('ceric');
      },
      isCurrentProposalRapidAccess() {
        return this.currentProposal?.general?.proposal_type === 'Rapid access';
      },
      technicalEvaluationComment() {
        if (this.isCurrentProposalCeric) return '-';
        // temporary change - to delete when changing cryo RA inhouse_grade into technical_evaluation
        if (this.isCurrentProposalRapidAccess && !this.isSynchrotron) {
          return this.currentProposal?.inhouse_grade?.comment ?? '-';
        }
        return this.currentProposal?.technical_evaluation?.comments_for_user ?? '-';
      },
      technicalEvaluationResult() {
        if (this.isCurrentProposalCeric) return '-';
        // temporary change - to delete when changing cryo RA inhouse_grade into technical_evaluation
        if (this.isCurrentProposalRapidAccess && !this.isSynchrotron) {
          return this.currentProposal?.inhouse_grade?.acceptance_mark.toUpperCase() ?? '-';
        }
        return this.currentProposal?.technical_evaluation?.feasibility ?? '-';
      },
      evaluationTableData() {
        return [
          {
            evaluationType: 'Review Committee comment',
            evaluationComment: this.currentProposal?.evaluations_data?.granted_comments ?? '-',
            evaluationResult: this.currentProposal?.evaluations_data?.acceptance_mark.toUpperCase() ?? '-',
          },
          {
            evaluationType: 'Technical evaluation',
            evaluationComment: this.technicalEvaluationComment,
            evaluationResult: this.technicalEvaluationResult,
          },
          {
            evaluationType: 'Safety evaluation',
            evaluationComment: this.currentProposal?.evaluations_data?.safety_evaluation_comments ?? '-',
            evaluationResult: this.currentProposal?.experiment_color?.safety_evaluation?.color ?? '-',
          },
          {
            evaluationType: 'Radiation evaluation',
            evaluationComment: this.currentProposal?.evaluations_data?.radiation_evaluation_comments ?? '-',
            evaluationResult: this.currentProposal?.experiment_color?.radiation_evaluation?.color ?? '-',
          },
        ];
      },
      asdTableData() {
        return [
          {
            evaluationType: 'Safety evaluation',
            evaluationComment:
              this.currentProposal?.additional_sample_declaration?.safety_evaluation?.comments_user ?? '-',
            evaluationResult: this.currentProposal?.additional_sample_declaration?.safety_evaluation?.status ?? '-',
          },
          {
            evaluationType: 'Radiation evaluation',
            evaluationComment:
              this.currentProposal?.additional_sample_declaration?.radiation_evaluation?.comments_user ?? '-',
            evaluationResult: this.currentProposal?.additional_sample_declaration?.radiation_evaluation?.status ?? '-',
          },
        ];
      },
      teamTableData() {
        let teamWithRoles = [];
        if (this.currentProposal?.team_with_roles) {
          teamWithRoles = Object.values(this.currentProposal?.team_with_roles);
        }
        const preparedTeamWithRoles = [];

        if (teamWithRoles.length > 0) {
          teamWithRoles.forEach((item) => {
            const itemRoles = this.rolesFormatter(item.roles);
            preparedTeamWithRoles.push({ teamMember: `${item.first_name} ${item.last_name}, roles:${itemRoles}` });
          });
          return preparedTeamWithRoles;
        }
        return [];
      },
    },
    methods: {
      async createNewProposal() {
        const { data } = await this.axios.post(`documents/new/${this.url.infrastructure}`);
        if (!data.error) {
          await this.$router.push(`/proposals/${this.url.proposalFormUrlPart}${data}`);
          this.$notify({ type: 'success', title: 'Proposal created successfully!' });
        } else {
          this.$notify({ type: 'error', title: 'Proposal creation error' });
        }
      },
      set_refresh(value) {
        this.refresh = value;
      },
      openEvaluationModal(currentProposal) {
        if (currentProposal !== null) {
          this.currentProposal = currentProposal;
          this.$refs.evaluationModal.show();
        }
      },
      rolesFormatter(roles) {
        return roles.map((role) => ` ${this.formatRole(role)}`);
      },
    },
  };
</script>
<style lang="scss"
       scoped>
  .card {
    margin: 4rem;
  }
</style>
